import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api, SUBJECTS, CLASSES } from "@/lib/api";
import { absoluteFileUrl } from "@/lib/utils_ext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PdfViewerModal from "@/components/PdfViewerModal";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileText, ChevronRight, Bookmark, Download, Search, Home, Eye } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";

export default function BrowseNotes() {
  const [sp, setSp] = useSearchParams();
  const { user } = useAuth();
  const [cls, setCls] = useState(sp.get("class") || "");
  const [subject, setSubject] = useState(sp.get("subject") || "");
  const [chapter, setChapter] = useState(sp.get("chapter") || "");
  const [q, setQ] = useState("");
  const [chapters, setChapters] = useState([]);
  const [notes, setNotes] = useState([]);
  const [bookmarks, setBookmarks] = useState(new Set());
  const [viewingNote, setViewingNote] = useState(null);

  const loadBookmarks = useCallback(async () => {
    if (!user) return;
    try {
      const r = await api.get("/bookmarks");
      setBookmarks(new Set(r.data.filter(b => b.resource_type === "note").map(b => b.resource_id)));
    } catch (err) {
      console.error("bookmarks load failed", err);
    }
  }, [user]);

  useEffect(() => { loadBookmarks(); }, [loadBookmarks]);

  useEffect(() => {
    if (cls && subject) {
      api.get("/notes/chapters", { params: { class_level: cls, subject } })
        .then(r => setChapters(r.data)).catch(() => setChapters([]));
    } else setChapters([]);
  }, [cls, subject]);

  useEffect(() => {
    const params = {};
    if (cls) params.class_level = cls;
    if (subject) params.subject = subject;
    if (chapter) params.chapter = chapter;
    if (q) params.q = q;
    api.get("/notes", { params }).then(r => setNotes(r.data)).catch(() => setNotes([]));
    // Sync URL
    const next = {};
    if (cls) next.class = cls;
    if (subject) next.subject = subject;
    if (chapter) next.chapter = chapter;
    setSp(next, { replace: true });
  }, [cls, subject, chapter, q, setSp]);

  const toggleBookmark = async (id) => {
    if (!user) return toast.error("Sign in to bookmark");
    try {
      const r = await api.post("/bookmarks/toggle", { resource_type: "note", resource_id: id });
      setBookmarks(prev => {
        const s = new Set(prev);
        if (r.data.status === "added") s.add(id); else s.delete(id);
        return s;
      });
    } catch (err) {
      console.error("bookmark toggle failed", err);
    }
  };

  const breadcrumb = [
    { label: "Home", value: null, onClick: () => { setCls(""); setSubject(""); setChapter(""); } },
    ...(cls ? [{ label: `Class ${cls}`, onClick: () => { setSubject(""); setChapter(""); } }] : []),
    ...(subject ? [{ label: subject, onClick: () => setChapter("") }] : []),
    ...(chapter ? [{ label: chapter }] : []),
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 pt-32 pb-16">
        <div className="text-xs uppercase tracking-widest font-semibold text-primary mb-3">Notes library</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter">Chapter-organized notes</h1>
        <p className="text-muted-foreground mt-3">Browse Class → Subject → Chapter → Topic. Bookmark what matters.</p>

        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 mt-8 flex-wrap text-sm" data-testid="notes-breadcrumb">
          {breadcrumb.map((b, i) => (
            <div key={i} className="flex items-center gap-2">
              {i > 0 && <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
              {b.onClick ? (
                <button onClick={b.onClick} className="text-muted-foreground hover:text-foreground flex items-center gap-1">
                  {i === 0 && <Home className="w-3.5 h-3.5" />} {b.label}
                </button>
              ) : (
                <span className="font-semibold">{b.label}</span>
              )}
            </div>
          ))}
        </nav>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-12 gap-8">
          <aside className="md:col-span-3 space-y-6 md:sticky md:top-28 self-start">
            <div className="glass rounded-2xl p-5 space-y-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mb-2">Class</div>
                <Select value={cls} onValueChange={setCls}>
                  <SelectTrigger className="h-11 rounded-xl" data-testid="filter-class"><SelectValue placeholder="Choose class" /></SelectTrigger>
                  <SelectContent>{CLASSES.map(c => <SelectItem key={c} value={c}>Class {c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mb-2">Subject</div>
                <Select value={subject} onValueChange={setSubject}>
                  <SelectTrigger className="h-11 rounded-xl" data-testid="filter-subject"><SelectValue placeholder="Choose subject" /></SelectTrigger>
                  <SelectContent>{SUBJECTS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            {chapters.length > 0 && (
              <div className="glass rounded-2xl p-5">
                <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mb-3">Chapters</div>
                <div className="space-y-1">
                  {chapters.map(c => (
                    <button key={c.chapter} onClick={() => setChapter(chapter === c.chapter ? "" : c.chapter)}
                      data-testid={`chapter-${c.chapter}`}
                      className={`w-full text-left px-3 py-2 rounded-xl text-sm transition-colors ${chapter === c.chapter ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>
                      <div className="font-medium truncate">{c.chapter}</div>
                      <div className={`text-[10px] ${chapter === c.chapter ? "text-primary-foreground/70" : "text-muted-foreground"}`}>{c.note_count} topics</div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </aside>

          <main className="md:col-span-9">
            <div className="relative mb-6">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search within results…"
                data-testid="notes-search-input" className="h-12 rounded-xl pl-11" />
            </div>
            {!cls || !subject ? (
              <div className="glass rounded-3xl p-16 text-center">
                <FileText className="w-12 h-12 text-primary mx-auto mb-4 opacity-60" />
                <div className="font-display text-2xl font-bold">Pick a class and subject to get started</div>
                <p className="text-muted-foreground mt-2">Use the filters on the left.</p>
              </div>
            ) : notes.length === 0 ? (
              <div className="glass rounded-3xl p-16 text-center">
                <div className="text-muted-foreground">No notes match yet.</div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="notes-grid">
                {notes.map(n => (
                  <div key={n.note_id} className="glass rounded-2xl p-5 card-3d" data-testid={`note-${n.note_id}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-primary" strokeWidth={1.8} />
                      </div>
                      <button onClick={() => toggleBookmark(n.note_id)} data-testid={`bookmark-${n.note_id}`}
                        className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${bookmarks.has(n.note_id) ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>
                        <Bookmark className="w-4 h-4" strokeWidth={1.8} fill={bookmarks.has(n.note_id) ? "currentColor" : "none"} />
                      </button>
                    </div>
                    <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mb-1">
                      {n.chapter}
                    </div>
                    <h3 className="font-display text-lg font-bold leading-tight mb-2">{n.title}</h3>
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-4">{n.description}</p>
                    <div className="flex items-center justify-between text-xs text-muted-foreground gap-2">
                      <span className="truncate">by {n.uploader_name}</span>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <Button size="sm" variant="outline" onClick={() => setViewingNote(n)}
                          data-testid={`view-note-${n.note_id}`}
                          className="rounded-full h-8 text-xs">
                          <Eye className="w-3 h-3 mr-1" /> Read
                        </Button>
                        {n.file_url && (
                          <Button size="sm" onClick={() => window.open(absoluteFileUrl(n.file_url), "_blank")}
                            className="rounded-full h-8 text-xs bg-primary hover:bg-primary/90">
                            <Download className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </main>
        </div>
      </div>
      <PdfViewerModal
        open={!!viewingNote}
        onClose={() => setViewingNote(null)}
        note={viewingNote}
        onOpenNote={(n) => setViewingNote(n)}
      />
      <Footer />
    </div>
  );
}
